import React, { useContext } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { Box, useMediaQuery } from '@chakra-ui/react';
import "./App.css";
import AuthContext, { AuthProvider } from './components/services/AuthContext';
import { TranslationProvider } from './contexts/TranslationContext';
import { SidebarProvider } from './components/utils/SidebarContext';
import { Text, Button, Image, Flex, VStack } from "@chakra-ui/react";
import { DownloadIcon } from "@chakra-ui/icons";
// Import route protection components
import {
  AdminRoute,
  InstructorRoute,
  StudentRoute,
  AdminInstructorRoute,
  AuthenticatedRoute,
  PublicRoute,
} from './components/shared/ProtectedRoute';

// Import all your pages
import LoginPage from './components/pages/LoginPage';
import ForgotPasswordPage from './components/pages/ForgotPasswordPage';
import ResetPasswordPage from './components/pages/ResetPasswordPage';

// Public pages
import HomePage from './components/pages/HomePage';
import PacksPage from './components/pages/PacksPage';
import NiveauPacks from './components/pages/NiveauPacks';
import PackDetails from './components/pages/PackDetails';
import AboutPage from './components/pages/AboutPage';
import VisionPage from './components/pages/VisionPage';
import ContactPage from './components/pages/ContactPage';
import CourseDiscovery from './components/pages/CourseDiscovery';
import NiveauDetails from './components/pages/NiveauDetails';

// Role-based dashboards
import AdminDashboard from './components/pages/AdminDashboard';
import AdminStudentFeedback from './components/pages/AdminStudentFeedback';
import InstructorDashboard from './components/pages/InstructorDashboard';
import StudentDashboard from './components/pages/StudentDashboard';

// Admin pages
import UserAdminManagement from './components/pages/UserAdminManagement';
import MaylawanAdmin from './components/pages/MaylawanAdmin.jsx';
import CourseAdminManagement from './components/pages/CourseAdminManagement';
import EditierAdminCourse from './components/pages/EditierAdminCourse';
import AdminEvents from './components/pages/AdminEvent';
import EducationManagement from './components/pages/EducationManagement';
import RegistrationManagement from './components/pages/RegistrationManagement';
import AdminPayments from './pages/AdminPayments';
import BacReviewRegistrations from './components/pages/BacReviewRegistrations';

// Instructor pages
import QCMManagementPage from './components/pages/QCMManagementPage';

// Student pages
import Courses from './components/pages/Courses';
import CourseDetails from './components/pages/CourseDetails';
import QCMList from './components/pages/QCMList';
import QCMTestPage from './components/pages/QCMTestPage';
import StudentQCMPage from './components/pages/StudentQCMPage';
import UserUploadsPage from './components/pages/UserUploadsPage';
import AttendancePopup from './components/shared/AttendancePopup';
import AttendanceControl from './components/pages/AttendanceControl';

// Shared pages
import Events from './components/pages/Events';
import Notifications from './components/pages/Notifications';
import Reclamations from './components/pages/Reclamations';

// Legacy dashboard (for backward compatibility)
import InstructorEvents from './components/pages/InstructorEvents';
import InstructorFileUpload from './components/pages/InstructorFileUpload';
import CourseDetailsInstructor from './components/pages/CourseDetailsInstructor';
import PlatformGate from './components/shared/PlatformGate';
import InstructorCourseManagement from './components/pages/InstructorCoursemangement';
import AdminReclamationsManager from './components/pages/AdminReclamationsManager';
import InstructorCourses from './components/pages/InstructorCourses';
import WhatsAppNamesPage from './components/pages/WhatsAppNamesPage';
import AbsenceManagement from './components/pages/AdminAlert';
import PDFViewer from './components/pages/pdfviewer';
import PublicVideoPage from './components/pages/PublicVideoPage';
import RecruitmentFormPage from './components/pages/RecruitmentFormPage';
import AdminRecruitmentManagement from './components/pages/AdminRecruitmentManagement';
import AdminScheduleForm from './components/pages/AdminScheduleForm';
import ScheduleFormPublic from './components/pages/ScheduleFormPublic';
import SurveyCheck from './components/shared/SurveyCheck';
import BacReviewCheck from './components/shared/BacReviewCheck';
import GicLocationCheck from './components/shared/GicLocationCheck';
import AdminSurveyManagement from './components/pages/AdminSurveyManagement';
import MeetingRoom from './components/pages/MeetingRoom';
import MeetingsPage from './components/pages/MeetingsPage';
import AdminFeedbackVideos from './components/pages/AdminFeedbackVideos';
import LiveAttendanceReport from './components/pages/LiveAttendanceReport';
import InstructorSeancesReport from './components/pages/InstructorSeancesReport';



const App = () => {
  const [isFlutterWebView, setIsFlutterWebView] = React.useState(false);
  const { loading, user } = useContext(AuthContext);

  React.useEffect(() => {
    setIsFlutterWebView(isAccessedFromFlutterWebView());
  }, []);

  const isElectronApp = () => {
    const userAgent = navigator.userAgent.toLowerCase();
    return userAgent.includes('electron/') || userAgent.includes('cobradesktopapp');
  };

  if (loading) {
    return (
      <Box height="100vh" display="flex" alignItems="center" justifyContent="center">
        <Text>جاري التحميل...</Text>
      </Box>
    );
  }

  return (
    <div id="app-container">
      <AttendancePopup />
      {/* <ScheduleFormPopup /> */}
      <SurveyCheck />
      <BacReviewCheck />
      <GicLocationCheck />
      <AnimatePresence>
        <Box>
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={
              <PublicRoute>
                <HomePage />
              </PublicRoute>
            } />

            <Route path="/login" element={
              <PublicRoute redirectIfAuthenticated={true}>
                <LoginPage />
              </PublicRoute>
            } />

            <Route path="/forgot-password" element={
              <PublicRoute redirectIfAuthenticated={true}>
                <ForgotPasswordPage />
              </PublicRoute>
            } />

            <Route path="/reset-password" element={
              <PublicRoute redirectIfAuthenticated={true}>
                <ResetPasswordPage />
              </PublicRoute>
            } />

            <Route path="/packs" element={<PacksPage />} />
            <Route path="/niveau/:niveauId/packs" element={<NiveauPacks />} />
            <Route path="/pack/:packId" element={<PackDetails />} />

            <Route path="/discover" element={
              <PublicRoute>
                <CourseDiscovery />
              </PublicRoute>
            } />

            <Route path="/discover/:id" element={
              <PublicRoute>
                <NiveauDetails />
              </PublicRoute>
            } />

            <Route path="/about" element={
              <PublicRoute>
                <AboutPage />
              </PublicRoute>
            } />

            <Route path="/vision" element={
              <PublicRoute>
                <VisionPage />
              </PublicRoute>
            } />

            <Route path="/contact" element={
              <PublicRoute>
                <ContactPage />
              </PublicRoute>
            } />

            <Route path="/video" element={
              <PublicRoute>
                <PublicVideoPage />
              </PublicRoute>
            } />

            <Route path="/recrutement" element={
              <PublicRoute>
                <RecruitmentFormPage />
              </PublicRoute>
            } />

            {/* Legacy Dashboard Route - Redirect based on role */}


            {/* Admin Routes */}
            <Route path="/admin/live-attendance" element={
              <AdminRoute>
                <LiveAttendanceReport />
              </AdminRoute>
            } />
            <Route path="/admin/instructor-seances" element={
              <AdminRoute>
                <InstructorSeancesReport />
              </AdminRoute>
            } />


            <Route path="/admin/users" element={
              <AdminRoute>
                <UserAdminManagement />
              </AdminRoute>
            } />
            <Route path="/admin/maylawan" element={
              <AdminRoute>
                <MaylawanAdmin />
              </AdminRoute>
            } />
            <Route path="/admin/whatsapp-groups" element={
              <AdminRoute>
                <WhatsAppNamesPage />
              </AdminRoute>
            } />
            <Route path="/instructor/absencemanagement" element={
              <AdminRoute>
                <AbsenceManagement />
              </AdminRoute>
            } />
            <Route path="/attendance/control" element={
              <AdminInstructorRoute>
                <AttendanceControl />
              </AdminInstructorRoute>
            } />

            <Route path="/admin/users/create" element={
              <AdminRoute>
                <UserAdminManagement />
              </AdminRoute>
            } />

            <Route path="/admin/cours" element={
              <AdminRoute>
                <CourseAdminManagement />
              </AdminRoute>
            } />
            <Route path="/admin" element={
              <AdminRoute>
                <AdminDashboard />
              </AdminRoute>
            } />

            <Route path="/admin/courses" element={
              <AdminRoute>
                <CourseAdminManagement />
              </AdminRoute>
            } />
            <Route path="/admin/reclamation" element={
              <AdminRoute>
                <AdminReclamationsManager />
              </AdminRoute>
            } />

            <Route path="/admin/courses/create" element={
              <AdminRoute>
                <CourseAdminManagement />
              </AdminRoute>
            } />

            <Route path="/admin/courses/:id" element={
              <AdminRoute>
                <EditierAdminCourse />
              </AdminRoute>
            } />
            <Route path="/instructor/courses/:id" element={
              <InstructorRoute>
                <InstructorCourses />
              </InstructorRoute>
            } />
            <Route path="/instructor/abmanagement" element={
              <InstructorRoute>
                <AbsenceManagement />
              </InstructorRoute>
            } />

            <Route path="/admin/events" element={
              <AdminRoute>
                <AdminEvents />
              </AdminRoute>
            } />
            <Route path="/pdf-viewer" element={<PDFViewer />} />
            <Route path="/admin/homepage" element={
              <AdminRoute>
                <AdminDashboard />
              </AdminRoute>
            } />

            <Route path="/admin/statistics" element={
              <AdminRoute>
                <AdminDashboard />
              </AdminRoute>
            } />
            <Route path="/admin/student-feedback" element={
              <AdminRoute>
                <AdminStudentFeedback />
              </AdminRoute>
            } />

            <Route path="/admin/education" element={
              <AdminRoute>
                <EducationManagement />
              </AdminRoute>
            } />

            <Route path="/admin/registrations" element={
              <AdminRoute>
                <RegistrationManagement />
              </AdminRoute>
            } />

            <Route path="/admin/payments" element={
              <AdminRoute>
                <AdminPayments />
              </AdminRoute>
            } />

            <Route path="/admin/recrutement" element={
              <AdminRoute>
                <AdminRecruitmentManagement />
              </AdminRoute>
            } />

            <Route path="/admin/schedule-forms" element={
              <AdminRoute>
                <AdminScheduleForm />
              </AdminRoute>
            } />

            <Route path="/admin/surveys" element={
              <AdminRoute>
                <AdminSurveyManagement />
              </AdminRoute>
            } />

            <Route path="/admin/feedback-videos" element={
              <AdminRoute>
                <AdminFeedbackVideos />
              </AdminRoute>
            } />

            <Route path="/admin/bac-review" element={
              <AdminRoute>
                <BacReviewRegistrations />
              </AdminRoute>
            } />

            <Route path="/schedule/:id" element={
              <PublicRoute>
                <ScheduleFormPublic />
              </PublicRoute>
            } />

            {/* Instructor Routes */}
            <Route path="/instructor" element={
              <InstructorRoute>
                <InstructorDashboard />
              </InstructorRoute>
            } />



            <Route path="/instructor/qcm" element={
              <InstructorRoute>
                <QCMManagementPage />
              </InstructorRoute>
            } />



            <Route path="/instructor/uploads" element={
              <InstructorRoute>
                <UserUploadsPage />
              </InstructorRoute>
            } />

            <Route path="/instructor/comments/:id" element={
              <InstructorRoute>
                <CourseDetailsInstructor />
              </InstructorRoute>
            } />
            <Route path="/instructor/courses" element={
              <InstructorRoute>
                <InstructorCourseManagement />
              </InstructorRoute>
            } />
            <Route path="/instructor" element={
              <InstructorRoute>
                <InstructorDashboard />
              </InstructorRoute>
            } />
            <Route path="/instructor/upload" element={
              <InstructorRoute>
                <InstructorFileUpload />
              </InstructorRoute>
            } />
            <Route path="/instructor/events" element={
              <InstructorRoute>
                <InstructorEvents />
              </InstructorRoute>
            } />


            {/* Student Routes */}

            <Route path="/student/courses" element={
              <StudentRoute>
                <Courses />
              </StudentRoute>
            } />

            <Route path="/student/courses/:id" element={
              <StudentRoute>
                <CourseDetails />
              </StudentRoute>
            } />
            <Route path="/student" element={
              <StudentRoute>
                <StudentDashboard />
              </StudentRoute>
            } />

            <Route path="/student/qcms" element={
              <StudentRoute>
                <QCMList />
              </StudentRoute>
            } />

            <Route path="/student/qcms/:id/take" element={
              <StudentRoute>
                <QCMTestPage />
              </StudentRoute>
            } />

            <Route path="/student/qcms/:id/result" element={
              <StudentRoute>
                <StudentQCMPage />
              </StudentRoute>
            } />

            <Route path="/student/uploads" element={
              <StudentRoute>
                <UserUploadsPage />
              </StudentRoute>
            } />

            <Route path="/student/notifications" element={
              <StudentRoute>
                <Notifications />
              </StudentRoute>
            } />

            <Route path="/student/reclamations" element={
              <StudentRoute>
                <Reclamations />
              </StudentRoute>
            } />

            {/* Shared Authenticated Routes */}
            <Route path="/events" element={
              <StudentRoute>
                <Events />
              </StudentRoute>
            } />

            <Route path="/notifications" element={
              <StudentRoute>
                <Notifications />
              </StudentRoute>
            } />

            <Route path="/reclamations" element={
              <StudentRoute>
                <Reclamations />
              </StudentRoute>
            } />

            {/* Live Meeting Routes */}
            <Route path="/meetings" element={
              <AuthenticatedRoute>
                <MeetingsPage />
              </AuthenticatedRoute>
            } />
            <Route path="/meet/:roomName" element={
              <AuthenticatedRoute>
                <MeetingRoom />
              </AuthenticatedRoute>
            } />

            {/* Legacy routes for backward compatibility */}
            <Route path="/courses" element={
              <StudentRoute>
                <Courses />
              </StudentRoute>
            } />

            <Route path="/courses/:id" element={
              <StudentRoute>
                <CourseDetails />
              </StudentRoute>
            } />

            <Route path="/qcm" element={
              <StudentRoute>
                <QCMList />
              </StudentRoute>
            } />

            <Route path="/qcm/:id/take" element={
              <StudentRoute>
                <QCMTestPage />
              </StudentRoute>
            } />

            <Route path="/uploads" element={
              <StudentRoute>
                <UserUploadsPage />
              </StudentRoute>
            } />

            {/* Fallback */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Box>
      </AnimatePresence>
    </div>
  );
};




// Function to detect if the app is accessed from Flutter WebView
function isAccessedFromFlutterWebView() {
  const userAgent = navigator.userAgent;

  // Check for Flutter WebView specific user agent patterns
  const isFlutterWebView = userAgent.includes('Flutter') ||
    userAgent.includes('FlutterWebView') ||
    userAgent.includes('MyFlutterApp');

  // Check for Android WebView indicator
  const isAndroidWebView = userAgent.includes('wv');

  // Check for custom header or parameter
  const hasFlutterIdentifier = window.location.search.includes('flutter=true') ||
    document.referrer.includes('flutter') ||
    sessionStorage.getItem('isFlutterApp') === 'true';

  return isFlutterWebView || hasFlutterIdentifier || isAndroidWebView;
}

export default function MainApp() {
  return (
    <AuthProvider>
      <TranslationProvider>
        <SidebarProvider>
          <App />
        </SidebarProvider>
      </TranslationProvider>
    </AuthProvider>
  );
}

